import { useEffect, useState } from "react";
import { Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const NEXT = { new: "confirmed", confirmed: "preparing", preparing: "ready", ready: "completed" };
const NEXT_LABEL = { new: "Accept", confirmed: "Start preparing", preparing: "Mark ready", ready: "Complete" };
const EMPTY_ITEM = { name: "", description: "", price: "", category: "platter", serves: 1, available: true };

const dueBadge = (o) => {
  if (o.order_type !== "pickup" || !o.collection_date || ["completed", "cancelled"].includes(o.status)) return null;
  const due = new Date(`${o.collection_date}T${o.collection_time || "12:00"}:00`);
  const hours = (due - Date.now()) / 36e5;
  let label, cls;
  if (hours < 0) {
    label = "Overdue"; cls = "bg-red-100 text-red-700 border-red-300";
  } else if (hours < 24) {
    label = `Due in ${Math.max(1, Math.round(hours))}h`; cls = "bg-red-100 text-red-700 border-red-300";
  } else if (hours < 48) {
    label = "Due tomorrow"; cls = "bg-amber-100 text-amber-800 border-amber-300";
  } else {
    label = `Due in ${Math.round(hours / 24)}d`; cls = "bg-slate-100 text-slate-600 border-slate-300";
  }
  return <span data-testid={`order-due-${o.id}`} className={`ml-2 inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}>{label}</span>;
};

const sortOrders = (rows) => [...rows].sort((a, b) => {
  const done = (o) => (["completed", "cancelled"].includes(o.status) ? 1 : 0);
  if (done(a) !== done(b)) return done(a) - done(b);
  const when = (o) => (o.collection_date ? `${o.collection_date}T${o.collection_time || "12:00"}` : "9999");
  return when(a).localeCompare(when(b));
});

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pickup");
  const [itemOpen, setItemOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_ITEM);

  const load = () => api.get("/orders", { params: { order_type: tab } }).then((r) => setOrders(sortOrders(r.data))).catch((e) => toast.error(formatApiError(e)));
  const loadItems = () => api.get("/order-items").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [tab]);
  useEffect(() => { loadItems(); }, []);

  const advance = async (o) => {
    try {
      await api.patch(`/orders/${o.id}/status`, { status: NEXT[o.status] });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancel = async (o) => {
    try {
      await api.patch(`/orders/${o.id}/status`, { status: "cancelled" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveItem = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: Number(form.price), serves: Number(form.serves) };
    try {
      if (editingItem) await api.patch(`/order-items/${editingItem.id}`, payload);
      else await api.post("/order-items", payload);
      toast.success(editingItem ? "Item updated" : "Item added");
      setItemOpen(false);
      setEditingItem(null);
      setForm(EMPTY_ITEM);
      loadItems();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleAvailable = async (item) => {
    try {
      await api.patch(`/order-items/${item.id}`, { available: !item.available });
      loadItems();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await api.delete(`/order-items/${item.id}`);
      loadItems();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const OrdersTable = ({ rows }) => (
    <div className="border border-border bg-white">
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            {["Ref", "Customer", "Items", "When / Where", "Total", "Paid", "Status", ""].map((h) => (
              <th key={h} className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id} data-testid={`order-row-${o.id}`} className="border-b border-border last:border-0 hover:bg-slate-50">
              <td className="px-5 py-3 font-mono text-xs font-bold">{o.order_ref}</td>
              <td className="px-5 py-3">
                <p className="font-medium">{o.customer_name}</p>
                <p className="text-xs text-muted-foreground">{o.phone || o.email}</p>
              </td>
              <td className="px-5 py-3 text-muted-foreground">{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</td>
              <td className="px-5 py-3 tabular-nums">{o.order_type === "pickup" ? `${o.collection_date} ${o.collection_time}` : o.table_ref}{dueBadge(o)}</td>
              <td className="px-5 py-3 font-semibold tabular-nums">{fmt(o.total)}</td>
              <td className="px-5 py-3"><StatusBadge status={o.paid ? "paid" : "unpaid"} /></td>
              <td className="px-5 py-3"><StatusBadge status={o.status} /></td>
              <td className="px-5 py-3">
                <div className="flex gap-2">
                  {NEXT[o.status] && (
                    <Button data-testid={`order-advance-${o.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => advance(o)}>{NEXT_LABEL[o.status]}</Button>
                  )}
                  {!["completed", "cancelled"].includes(o.status) && (
                    <Button data-testid={`order-cancel-${o.id}`} size="sm" variant="outline" className="rounded-md border-red-300 text-red-600 hover:bg-red-50" onClick={() => cancel(o)}>Cancel</Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-muted-foreground">No orders here yet.</td></tr>}
        </tbody>
      </table>
      </div>
    </div>
  );

  return (
    <div data-testid="orders-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Orders</h1>
          <p className="mt-1 text-sm text-muted-foreground">Buffet pickup orders and live on-site ordering from your public hub.</p>
        </div>
        <Button data-testid="manage-order-menu-button" onClick={() => { setEditingItem(null); setForm(EMPTY_ITEM); setItemOpen(true); }} className="rounded-md bg-ink hover:bg-ink-light">
          <Plus className="mr-2 h-4 w-4" /> Add Order Item
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-muted" data-testid="orders-tabs">
          <TabsTrigger value="pickup" data-testid="orders-tab-pickup">Buffet Pickup</TabsTrigger>
          <TabsTrigger value="onsite" data-testid="orders-tab-onsite">On-site Orders</TabsTrigger>
          <TabsTrigger value="menu" data-testid="orders-tab-menu">Order Menu</TabsTrigger>
        </TabsList>

        <TabsContent value="pickup" className="mt-4"><OrdersTable rows={orders} /></TabsContent>
        <TabsContent value="onsite" className="mt-4"><OrdersTable rows={orders} /></TabsContent>

        <TabsContent value="menu" className="mt-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items.map((i) => (
              <div key={i.id} data-testid={`order-item-${i.id}`} className={`border border-border bg-white p-5 ${i.available === false ? "opacity-60" : ""}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center bg-muted"><ShoppingBag className="h-4 w-4 text-ink" /></div>
                    <div>
                      <h3 className="font-heading text-sm font-bold">{i.name}</h3>
                      <p className="text-xs capitalize text-muted-foreground">{i.category} · serves {i.serves}</p>
                    </div>
                  </div>
                  <span className="font-heading text-base font-black text-brand tabular-nums">{fmt(i.price)}</span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{i.description}</p>
                <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
                  <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                    <Switch data-testid={`item-available-${i.id}`} checked={i.available !== false} onCheckedChange={() => toggleAvailable(i)} />
                    {i.available === false ? "Off" : "On sale"}
                  </label>
                  <div className="flex gap-2">
                    <Button data-testid={`edit-item-${i.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => { setEditingItem(i); setForm({ name: i.name, description: i.description || "", price: i.price, category: i.category, serves: i.serves, available: i.available !== false }); setItemOpen(true); }}>Edit</Button>
                    <Button data-testid={`delete-item-${i.id}`} size="sm" variant="outline" className="rounded-md border-red-300 text-red-600 hover:bg-red-50" onClick={() => deleteItem(i)}>Delete</Button>
                  </div>
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="col-span-3 border border-border bg-white px-5 py-10 text-center text-sm text-muted-foreground">No orderable items yet — add platters and trays your customers can order.</p>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="order-item-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingItem ? "Edit Item" : "Add Order Item"}</DialogTitle></DialogHeader>
          <form onSubmit={saveItem} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Name</Label><Input data-testid="item-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Description</Label><Input data-testid="item-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Price (£)</Label><Input data-testid="item-price-input" type="number" step="0.5" min="0" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Serves</Label><Input data-testid="item-serves-input" type="number" min="1" value={form.serves} onChange={(e) => setForm({ ...form, serves: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="item-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="platter">Platter</SelectItem>
                  <SelectItem value="side">Side</SelectItem>
                  <SelectItem value="dessert">Dessert</SelectItem>
                  <SelectItem value="drink">Drinks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Button data-testid="item-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">{editingItem ? "Save Changes" : "Add Item"}</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
